import React from 'react';
import { FaCopy, FaProjectDiagram, FaNetworkWired, FaImage, FaPrint, FaStore, FaWhatsapp } from 'react-icons/fa';

// Asumo que estos componentes y utils están en rutas relativas correctas
import ConteoCheck from '../ConteoCheck';
import RotacionBadge from '../RotacionBadge';
import NombreProductoTooltip from '../../NombreProductoTooltip';
import copyToClipboard from '../../clipboard';
import { validarUbicacion } from '../../ubicacionValidator';
import formatCurrency from '../../../utils/formatCurrency';
import { STORE_BASE_URL } from '../../../utils/storeConstants';
import { formatUltimaVentaSoloFecha } from './ProductSalesMetrics';
import SolicitudInternaButton from '../SolicitudInternaButton';

// NOTA: Asumo que Tooltip, getPriorityClasses y handleNavigateToShow son funciones/componentes
// disponibles en el ámbito global o provienen de otros imports que no me has proporcionado.
// He removido el uso de Tooltip y getPriorityClasses de la columna 'Prioridad' y 'SKU'
// para evitar errores de compilación, dejando solo la estructura básica requerida.
// Si son necesarios, deberás asegurar su importación en el archivo real.

const ProductTable = ({
    paginatedProducts,
    token,
    showActions,
    showPrintLabel,
    onProductSelect, // Si se usa en la tabla, lo mantengo aquí para pasarlo
    onUpdateStock,
    onOpenParentModal,
    onPrintLabel,
    formatAplicaciones,
    handleSort, // Función para el ordenamiento
    showStoreLink = false,
    showWhatsAppShare = false,
    showMeliLink = false,
    onShareWhatsApp = null,
    storeBaseUrl = STORE_BASE_URL
}) => {

    // Función auxiliar para navegar a la vista del producto (asume que existe)
    const handleNavigateToShow = (id) => {
        window.open(`/productos/vista/${id}`, '_blank');
    };

    const handleUpdateExistenciasClick = (e, producto) => {
        e.preventDefault();
        e.stopPropagation();
        if (!producto.es_padre && onUpdateStock) {
            onUpdateStock(producto);
        }
    };
    
    return (
        <div className="overflow-x-auto shadow-xl rounded-lg border-2 border-yellow-500">
            {/* Contenedor responsivo para asegurar la visibilidad en móviles */}
            <table className="min-w-full table-auto text-xs text-gray-700">
                <thead>
                    <tr className="bg-yellow-500 text-black uppercase tracking-wider border-b border-yellow-700">
                        {/* Columna de ACCIONES/CONTEO */}
                        <th className="p-3 text-center cursor-default">ACCIONES</th>
                        <th onClick={() => handleSort('ubicacion')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Ubicación</th>
                        <th onClick={() => handleSort('sku')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">SKU</th>
                        <th onClick={() => handleSort('codigo_barras')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Código de Barras</th>
                        <th onClick={() => handleSort('nombre_categoria')} className="p-3 text-center cursor-pointer font-bold hover:bg-yellow-400 transition-colors">Categoría</th>
                        <th onClick={() => handleSort('nombre_producto')} className="p-3 text-left cursor-pointer hover:bg-yellow-400 transition-colors">Descripción</th>
                        <th onClick={() => handleSort('aplicaciones_vehiculo')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Aplicación</th>
                        <th onClick={() => handleSort('medidas')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Medidas</th>
                        <th onClick={() => handleSort('nombre_marca')} className="p-3 text-left cursor-pointer hover:bg-yellow-400 transition-colors">Marca</th>
                        {showActions && (
                            <th onClick={() => handleSort('existencias')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Existencias</th>
                        )}
                        {showActions && (
                            <th onClick={() => handleSort('precio_venta')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Precio Venta</th>
                        )}
                        {showActions && (
                            <th onClick={() => handleSort('ventas_30_dias')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Ventas 30d</th>
                        )}
                        {showActions && (
                            <th onClick={() => handleSort('cantidad_ventas')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Total ventas</th>
                        )}
                        {showActions && (
                            <th onClick={() => handleSort('fecha_ultima_venta')} className="p-3 text-center cursor-pointer hover:bg-yellow-400 transition-colors">Última venta</th>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {paginatedProducts.map((producto, index) => (
                        <tr 
                            key={producto.id_producto} 
                            className={`hover:bg-yellow-100 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        >
                            <td className="p-3 text-center whitespace-nowrap">
                                <div className="flex items-center justify-center gap-2">
                                    <RotacionBadge
                                        estadoRotacion={producto.estado_rotacion}
                                        ventas30={producto.ventas_30_dias}
                                        fechaUltimaVenta={producto.fecha_ultima_venta}
                                        tooltipId={`rot-table-${producto.id_producto}`}
                                    />
                                    {/* Icono de Conteo/Padre */}
                                    {producto.es_padre ? (
                                        <FaNetworkWired className="h-5 w-5 text-yellow-500" title="Producto Padre" />
                                    ) : (
                                        <ConteoCheck 
                                            show={producto.tieneMovimientosRecientes} 
                                            empleado={producto.nombre_empleado} 
                                            fecha={producto.fecha_ultimo_movimiento}
                                            tooltipId={`conteo-table-${producto.id_producto}`}
                                        />
                                    )}
                                    {/* Botón de Imprimir Etiqueta */}
                                    {showPrintLabel && (
                                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrintLabel(producto); }} className="text-pink-400 hover:text-pink-600" title="Imprimir Etiqueta">
                                            <FaPrint className="w-5 h-5" />
                                        </button>
                                    )}
                                    {token && (
                                        <SolicitudInternaButton
                                            token={token}
                                            idProducto={producto.id_producto}
                                            esPadre={Boolean(producto.es_padre)}
                                            nombreProducto={producto.nombre_producto}
                                            tone="dark"
                                        />
                                    )}
                                     {onProductSelect && (
                                       <button
                                         onClick={(e) => { e.preventDefault(); e.stopPropagation(); onProductSelect(producto); }}
                                         className="bg-yellow-500 text-black px-3 py-1 rounded-lg hover:bg-black hover:text-yellow-500 text-xs font-bold transition-colors duration-200"
                                       >
                                         Usar
                                       </button>
                                     )}

                                     {/* ICONOS DE ACCIÓN: Tienda, MercadoLibre, WhatsApp */}
                                    {(showStoreLink || showMeliLink || showWhatsAppShare) && (
                                        <>
                                        <div className="h-4 w-[1px] bg-gray-300 mx-0.5"></div>
                                        
                                        {showStoreLink && (
                                            <a 
                                            href={`${storeBaseUrl}/productos/${producto.id_producto}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-gray-600 hover:text-yellow-600 transition-all font-bold"
                                            title="Ver en Tienda Virtual"
                                            >
                                            <FaStore className="w-5 h-5" />
                                            </a>
                                        )}

                                        {showMeliLink && (
                                            <button 
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (producto.enlace_meli) {
                                                    window.open(producto.enlace_meli, '_blank');
                                                } else {
                                                    try {
                                                        const { default: axios } = await import('axios');
                                                        const res = await axios.get(`/api/productos/meli-link/${producto.id_producto}`);
                                                        if (res.data.status === 'success') {
                                                            window.open(res.data.enlace_meli, '_blank');
                                                        } else {
                                                            const { default: Swal } = await import('sweetalert2');
                                                            Swal.fire({
                                                                icon: 'info',
                                                                title: 'No Publicado',
                                                                text: 'Este producto no tiene una publicación activa en MercadoLibre.',
                                                                confirmButtonColor: '#ffc240',
                                                                confirmButtonText: 'Entendido'
                                                            });
                                                        }
                                                    } catch (err) {
                                                        console.error('Error fetching meli link:', err);
                                                    }
                                                }
                                            }}
                                            className="hover:scale-110 transition-all w-6 h-6 flex items-center justify-center p-0.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200"
                                            title="Ver en MercadoLibre"
                                            >
                                            <img 
                                                src="/images/mercado-libre.png" 
                                                alt="Meli Logo" 
                                                className="w-full h-full object-contain"
                                            />
                                            </button>
                                        )}

                                        {showWhatsAppShare && onShareWhatsApp && (
                                            <button 
                                            onClick={(e) => { e.stopPropagation(); onShareWhatsApp(producto); }}
                                            className="text-green-500 hover:text-green-600 transition-all"
                                            title="Compartir por WhatsApp"
                                            >
                                            <FaWhatsapp className="w-5 h-5" />
                                            </button>
                                        )}
                                        </>
                                    )}
                                </div>
                            </td>
                            <td className="p-3 text-center whitespace-nowrap">{validarUbicacion(producto.ubicacion)}</td>
                            <td className="p-3 text-center whitespace-nowrap">{producto.sku}</td>
                            <td className="p-3 text-center whitespace-nowrap">
                                {/* Enlace y botón de copia */}
                                <a 
                                    href={`/productos/vista/${producto.id_producto}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="flex items-center justify-center text-blue-600 hover:underline hover:text-blue-800"
                                    onClick={(e) => handleNavigateToShow(producto.id_producto)} 
                                >
                                    <button
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyToClipboard(producto.codigo_barras); }}
                                        className="text-blue-500 hover:text-blue-700 mr-2"
                                        title="Copiar Código"
                                    >
                                        <FaCopy className="w-4 h-4" />
                                    </button>
                                    {producto.codigo_barras}
                                </a>
                            </td>
                            <td className="p-3 text-center whitespace-nowrap font-bold">{producto.nombre_categoria}</td>
                            <td className="p-3 text-left max-w-xs truncate">
                                <div className='flex items-center'>
                                    {producto.es_padre && (
                                        <FaProjectDiagram 
                                            className="inline-block mr-2 text-blue-500 hover:text-blue-600 cursor-pointer w-5 h-5 flex-shrink-0"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenParentModal(producto); }}
                                            title="Ver Hijos"
                                        />
                                    )}
                                    <NombreProductoTooltip
                                        nombreProducto={producto.nombre_producto}
                                        idProducto={producto.id_producto}
                                    />
                                    {producto.tiene_imagenes && (
                                        <FaImage className="inline-block ml-2 text-purple-500 text-lg w-5 h-5 flex-shrink-0" title="Tiene imágenes" />
                                    )}
                                </div>
                            </td>
                            <td className="p-3 text-center text-xs max-w-[150px] truncate" title={producto.aplicaciones_vehiculo && producto.segmento_vehiculo !== 'universal' ? formatAplicaciones(producto.aplicaciones_vehiculo) : ''}>
                                {producto.aplicaciones_vehiculo && producto.segmento_vehiculo !== 'universal'
                                  ? formatAplicaciones(producto.aplicaciones_vehiculo)
                                  : '—'}
                            </td>
                            <td className="p-3 text-center whitespace-nowrap">{producto.medidas || 'N/A'}</td>
                            <td className="p-3 text-left whitespace-nowrap">{producto.nombre_marca}</td>

                            {showActions && (
                                <td
                                    className={`p-3 text-center whitespace-nowrap ${!producto.es_padre ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                                    onClick={(e) => handleUpdateExistenciasClick(e, producto)}
                                >
                                    <span className={`font-bold px-3 py-1 rounded-full ${!producto.es_padre ? 'bg-yellow-500 text-black hover:bg-black hover:text-yellow-500' : 'bg-gray-200 text-gray-500'}`}>
                                        {parseFloat(producto.existencias).toFixed(1)}
                                    </span>
                                </td>
                            )}

                            {showActions && (
                                <td className="p-3 text-center whitespace-nowrap text-green-600 font-bold">
                                    {producto.rango_precios_hijos && producto.rango_precios_hijos.precio_min !== undefined && producto.rango_precios_hijos.precio_max !== undefined
                                    ? producto.rango_precios_hijos.precio_min === producto.rango_precios_hijos.precio_max
                                    ? formatCurrency(producto.rango_precios_hijos.precio_min)
                                    : `${formatCurrency(producto.rango_precios_hijos.precio_min)} - ${formatCurrency(producto.rango_precios_hijos.precio_max)}`
                                    : formatCurrency(producto.precio_venta)}   
                                </td>
                            )}
                            {showActions && (
                                <td className="p-3 text-center whitespace-nowrap font-bold text-emerald-700">
                                    {Number(producto.ventas_30_dias) || 0}
                                </td>
                            )}
                            {showActions && (
                                <td className="p-3 text-center whitespace-nowrap">
                                    {Number(producto.cantidad_ventas) || 0}
                                </td>
                            )}
                            {showActions && (
                                <td className="p-3 text-center whitespace-nowrap text-xs">
                                    {formatUltimaVentaSoloFecha(producto.fecha_ultima_venta)}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ProductTable;
